import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';

// Container for the markdown content within a bot bubble.
// direction: rtl and font-family: Rubik are inherited from the parent MessageBubble,
// but we re-declare them here to be safe and to scope all descendant styles.
const MarkdownContainer = styled.div`
  direction: rtl;
  font-family: "Rubik", sans-serif;
  font-size: 0.95rem;
  line-height: 1.6;
  color: inherit;

  /* Remove default top-margin on first child and bottom-margin on last child
     so the container matches the bubble's padding exactly. */
  > *:first-child {
    margin-top: 0;
  }
  > *:last-child {
    margin-bottom: 0;
  }

  /* ── Paragraphs ─────────────────────────────────────────────── */
  p {
    margin: 0 0 0.6em;
  }

  /* ── Headings (scaled down to fit a chat bubble) ─────────────── */
  h1, h2, h3, h4, h5, h6 {
    font-family: "Rubik", sans-serif;
    color: #103C6E;
    margin: 0.8em 0 0.4em;
    line-height: 1.3;
  }
  h1 { font-size: 1.15rem; }
  h2 { font-size: 1.08rem; }
  h3 { font-size: 1.00rem; }
  h4, h5, h6 { font-size: 0.95rem; }

  /* ── Bold / italic ────────────────────────────────────────────── */
  strong {
    font-weight: 700;
    color: #103C6E;
  }
  em {
    font-style: italic;
  }

  /* ── Lists — use padding-inline-start so RTL bullets aren't clipped ── */
  ul, ol {
    margin: 0.4em 0 0.6em;
    padding-inline-start: 1.4em;
    padding-inline-end: 0;
  }
  li {
    margin-bottom: 0.25em;
  }
  /* Nested lists */
  li > ul, li > ol {
    margin-top: 0.2em;
    margin-bottom: 0.2em;
  }

  /* ── Inline code ───────────────────────────────────────────────── */
  code {
    background: #f0f4f8;
    border: 1px solid #d0dae6;
    border-radius: 3px;
    padding: 0.1em 0.35em;
    font-size: 0.88em;
    font-family: "Courier New", monospace;
    direction: ltr;
    unicode-bidi: embed;
  }

  /* ── Code blocks ───────────────────────────────────────────────── */
  pre {
    background: #f0f4f8;
    border: 1px solid #d0dae6;
    border-radius: 6px;
    padding: 10px 12px;
    overflow-x: auto;
    margin: 0.5em 0;
    direction: ltr;
    unicode-bidi: embed;

    code {
      background: none;
      border: none;
      padding: 0;
      font-size: 0.88em;
    }
  }

  /* ── Blockquote ────────────────────────────────────────────────── */
  blockquote {
    border-inline-start: 3px solid #103C6E;
    margin: 0.5em 0;
    padding: 0.3em 0.8em;
    color: #555;
    background: #f8f9fa;
    border-radius: 0 4px 4px 0;
  }

  /* ── Horizontal rule ───────────────────────────────────────────── */
  hr {
    border: none;
    border-top: 1px solid #dde3ea;
    margin: 0.8em 0;
  }

  /* ── Links ─────────────────────────────────────────────────────── */
  a {
    color: #103C6E;
    text-decoration: underline;
    word-break: break-all;
  }
  a:hover {
    color: #0a2a4d;
  }

  /* ── Tables — scrollable wrapper is applied via TableWrapper below ── */
  table {
    border-collapse: collapse;
    width: 100%;
    font-size: 0.88rem;
    min-width: 280px;
  }
  thead {
    background: #103C6E;
    color: white;
  }
  th, td {
    border: 1px solid #c8d4e0;
    padding: 6px 10px;
    text-align: right;
    vertical-align: top;
  }
  tbody tr:nth-child(even) {
    background: #f0f4f8;
  }
  tbody tr:hover {
    background: #e4ecf5;
  }
`;

// Wraps <table> elements so they scroll horizontally on narrow bubbles
// without overflowing the bubble container.
const TableWrapper = styled.div`
  overflow-x: auto;
  margin: 0.5em 0;
  border-radius: 6px;
  /* Thin scrollbar on WebKit */
  &::-webkit-scrollbar {
    height: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #b0c4d8;
    border-radius: 3px;
  }
`;

// Custom renderer for <table> that wraps it in a scrollable container
const renderTable = ({ children, ...props }: React.HTMLAttributes<HTMLTableElement>) => (
  <TableWrapper>
    <table {...props}>{children}</table>
  </TableWrapper>
);

// Custom renderer for <a> that opens links in a new tab safely
const renderLink = ({
  href,
  children,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
    {children}
  </a>
);

const COMPONENTS = {
  table: renderTable,
  a: renderLink,
} as const;

type MarkdownMessageProps = {
  text: string;
};

const MarkdownMessage: React.FC<MarkdownMessageProps> = ({ text }) => (
  <MarkdownContainer>
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTS}>
      {text}
    </ReactMarkdown>
  </MarkdownContainer>
);

export default MarkdownMessage;
